import unlockAcademics from '@backrs_ui/common-ui/src/assets/images/unlocks/unlock_academics_full.png';
import unlockAchievement from '@backrs_ui/common-ui/src/assets/images/unlocks/unlock_achievement_full.png';
import unlockCommunities from '@backrs_ui/common-ui/src/assets/images/unlocks/unlock_communities_full.png';
import unlockSkills from '@backrs_ui/common-ui/src/assets/images/unlocks/unlock_skills_full.png';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { TrackAssessmentCategory } from '@backrs_ui/common-ui/src/models/trackAssessment';

import { Box, SxProps, Theme, Typography } from '@mui/material';

export const COLUMN_WIDTH = '200px';
export const NAME_COLUMN_WIDTH = '140px';
export const IMAGE_COLUMN_WIDTH = '220px';
const MIN_HEIGHT = '100px';

export const CATEGORY_IMAGES: Record<TrackAssessmentCategory['slug'], string> = {
  'academic-foundations': unlockAcademics,
  'real-world-experience': unlockAchievement,
  'mindsets-and-skills': unlockSkills,
  'community-of-success': unlockCommunities,
  none: '',
};

export type UnlockItemProps = Readonly<{
  category: TrackAssessmentCategory;
  strengths: string[];
  opportunitiesToGrow: string[];
  isMobile?: boolean;
  isEmptyState?: boolean;
}>;

export const UnlockItem = ({
  category,
  strengths,
  opportunitiesToGrow,
  isMobile = false,
  isEmptyState = false,
}: UnlockItemProps) => {
  const image = CATEGORY_IMAGES[category.slug];

  return (
    <Box sx={unlockItemContainerSx(isMobile, isEmptyState)}>
      <Box sx={imageContainerSx(isMobile)}>
        <Box
          component="img"
          src={image}
          alt={category.name}
          sx={unlockItemImageSx(isMobile, isEmptyState)}
        />
      </Box>
      <Box sx={tableContainerSx(isMobile)}>
        {/* Category Name Column */}
        <Box sx={categoryColumnSx(isMobile, isEmptyState)}>
          {!isEmptyState && (
            <Typography variant="text-sm-regular" sx={unlockItemTitleSx}>
              {category.name}
            </Typography>
          )}
        </Box>

        {/* Strengths Column */}
        <Box sx={sectionContainerSx(isMobile, isEmptyState)}>
          {!isEmptyState &&
            strengths.map((strength, index) => (
              <Box key={`strength-${index}`} sx={itemContainerSx}>
                <Box sx={iconContainerStrengthSx}>
                  <AppIcon iconName="Check" diameter="20px" color="Green/500" strokeWidth="bold" />
                </Box>
                <Typography variant="text-sm-regular" sx={itemTextSx}>
                  {strength.replace(/\//g, '/\u200B')}
                </Typography>
              </Box>
            ))}
        </Box>

        {/* Opportunities Column */}
        <Box sx={sectionContainerSx(isMobile, isEmptyState)}>
          {!isEmptyState &&
            opportunitiesToGrow.map((opportunity, index) => (
              <Box key={`opportunity-${index}`} sx={itemContainerSx}>
                <Box sx={iconContainerOpportunitySx}>
                  <AppIcon
                    iconName="Target03"
                    diameter="24px"
                    color="Colors/Foreground/fg-brand-primary"
                  />
                </Box>
                <Typography variant="text-sm-regular" sx={itemTextSx}>
                  {opportunity.replace(/\//g, '/\u200B')}
                </Typography>
              </Box>
            ))}
        </Box>
      </Box>
    </Box>
  );
};

const unlockItemContainerSx =
  (isMobile: boolean, isEmptyState = false): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    width: '100%',
    alignItems: 'stretch',
    gap: isMobile ? 0 : backrs.spacing['spacing-2xl'],
    backgroundColor: backrs.colors['Colors/Base/White'],
    borderTop: `1px solid ${isEmptyState ? backrs.colors['Colors/Foreground/fg-quinary'] : backrs.colors['Colors/Foreground/fg-secondary']}`,
  });

const imageContainerSx =
  (isMobile: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: isMobile ? '100%' : IMAGE_COLUMN_WIDTH,
    flexShrink: 0,
    display: 'flex',
    alignItems: isMobile ? 'center' : 'stretch',
    flexDirection: isMobile ? 'row' : 'column',
    gap: isMobile ? '12px' : 0,
    padding: isMobile ? '0' : 0,
    maxHeight: isMobile ? '100px' : '100%',
    backgroundColor: backrs.colors['Blue/100'],
    justifyContent: 'center',
  });

const unlockItemImageSx =
  (isMobile: boolean, isEmptyState = false): SxProps<Theme> =>
  ({ backrs }) => ({
    height: '100%',
    width: '90%',
    objectFit: 'cover',
    opacity: isEmptyState ? 0.3 : 1,
    ...(!isMobile && {
      borderRight: `1px solid ${isEmptyState ? backrs.colors['Colors/Foreground/fg-quinary'] : backrs.colors['Colors/Foreground/fg-secondary']}`,
      borderTop: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
      height: '100%',
      maxHeight: '100%',
      width: '100%',
      objectFit: 'cover',
    }),
  });

const tableContainerSx =
  (isMobile: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    flex: 1,
    gap: isMobile ? 0 : backrs.spacing['spacing-2xl'],
    alignItems: 'flex-start',
  });

const categoryColumnSx =
  (isMobile: boolean, isEmptyState = false): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    height: '100%',
    minWidth: NAME_COLUMN_WIDTH,
    maxWidth: NAME_COLUMN_WIDTH,
    minHeight: MIN_HEIGHT,
    ...(isMobile && {
      padding: backrs.spacing['spacing-2xl'],
      width: '100%',
      minWidth: 'auto',
      maxWidth: 'none',
      minHeight: 'auto',
      borderTop: `1px solid ${isEmptyState ? backrs.colors['Colors/Foreground/fg-quinary'] : backrs.colors['Colors/Foreground/fg-secondary']}`,
    }),
  });

const sectionContainerSx =
  (isMobile: boolean, isEmptyState = false): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: backrs.spacing['spacing-sm'],
    padding: backrs.spacing['spacing-2xl'],
    alignItems: 'flex-start',
    minWidth: 0, // Allow flex item to shrink below content size
    ...(isMobile && {
      width: '100%',
      borderTop: `1px solid ${isEmptyState ? backrs.colors['Colors/Foreground/fg-quinary'] : backrs.colors['Colors/Foreground/fg-secondary']}`,
    }),
  });

const itemContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-sm'],
  width: '100%',
  minWidth: 0,
});

const iconContainerOpportunitySx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const iconContainerStrengthSx: SxProps<Theme> = {
  ...iconContainerOpportunitySx,
  marginTop: '0px',
  marginBottom: '-2px',
};

const itemTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
});

const unlockItemTitleSx: SxProps<Theme> = ({ typography }) => ({
  fontStyle: 'italic',
  fontWeight: 500,
  ...typography['text-sm-regular'],
});
